import { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as EditIcon } from 'src/pages/AIListening/Main/CampaignCard/svgs/edit-2.svg'
import { ReactComponent as TrashIcon } from 'src/pages/AIListening/Main/CampaignCard/svgs/trash.svg'
import styles from './projectActions.module.scss'

interface ProjectActionsProps {
  onDelete: () => void
  onEdit: () => void
}

export const ProjectActions: FC<ProjectActionsProps> = ({
  onDelete,
  onEdit,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState({ right: 0, top: 0 })
  const ref = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return undefined

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      const isInsideTrigger = ref.current?.contains(target)
      const isInsideDropdown = dropdownRef.current?.contains(target)

      if (isInsideTrigger || isInsideDropdown) {
        return
      }

      setMenuOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('touchstart', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('touchstart', handleOutsideClick)
    }
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) return undefined

    const updateMenuPosition = () => {
      const wrapper = ref.current
      if (!wrapper) return

      const rect = wrapper.getBoundingClientRect()

      setMenuPosition({
        right: window.innerWidth - rect.right,
        top: rect.bottom + 8,
      })
    }

    updateMenuPosition()

    window.addEventListener('resize', updateMenuPosition)
    window.addEventListener('scroll', updateMenuPosition, true)

    return () => {
      window.removeEventListener('resize', updateMenuPosition)
      window.removeEventListener('scroll', updateMenuPosition, true)
    }
  }, [menuOpen])

  const runAction = (action: () => void) => {
    setMenuOpen(false)
    action()
  }

  return (
    <div className={styles.menuWrapper} ref={ref}>
      <button
        className={styles.menuBtn}
        onClick={() => setMenuOpen((prev) => !prev)}
        type="button"
      >
        <span className={styles.menuDot} />
        <span className={styles.menuDot} />
        <span className={styles.menuDot} />
      </button>
      {menuOpen &&
        createPortal(
          <div
            className={styles.menuDropdown}
            ref={dropdownRef}
            style={{
              right: `${menuPosition.right}px`,
              top: `${menuPosition.top}px`,
            }}
          >
            <button
              className={styles.menuItem}
              onClick={() => runAction(onEdit)}
              type="button"
            >
              <EditIcon className={styles.menuItemIcon} />
              Edit
            </button>
            <button
              className={styles.menuItem}
              onClick={() => runAction(onDelete)}
              type="button"
            >
              <TrashIcon className={styles.menuItemIcon} />
              Delete
            </button>
          </div>,
          document.body
        )}
    </div>
  )
}
